import { BitmapFont, Asset, assetManager, error, ImageAsset, JsonAsset, sp, SpriteFrame, TextAsset, Texture2D } from 'cc';
import { DEV } from 'cc/env';
import FntParser from './FontParser';
import AssetCache from './AssetCache';
import { extname } from '../../Utils/Utility';

namespace RemoteLoader {
    const timestampWhenLoadScript = new Date().getTime();
    const urlWithTimestamp = (url: string) => `${url + (url.includes('?') ? '&' : '?')}t=${timestampWhenLoadScript}`;

    const cache = new AssetCache();
    const assetTypeToLoaderMap: Map<typeof Asset, Function> = new Map<typeof Asset, Function>([
        [JsonAsset, loadJson],
        [SpriteFrame, loadSpriteFrame],
        [sp.SkeletonData, loadSkeletonData],
        [BitmapFont, loadBitmapFont],
    ]);

    export function getCachedAsset<T extends typeof Asset>(url: string, assetType: T): InstanceType<T> | null {
        return cache.get(assetType, url);
    }

    export async function load<T extends typeof Asset>(url: string, assetType: T): Promise<InstanceType<T>>;
    export async function load<T extends typeof Asset>(assetType: T, args: string[]): Promise<InstanceType<T>>;
    export async function load<T extends typeof Asset>(arg1: string | T, arg2: T | string[]): Promise<InstanceType<T>> {
        let assetType: typeof Asset;
        let args: any[];
        if (typeof arg1 === 'string') {
            // 第一個簽名：url 和 assetType
            assetType = arg2 as T;
            args = [arg1];
        } else {
            // 第二個簽名：assetType 和 args 陣列
            assetType = arg1;
            args = Array.isArray(arg2) ? arg2 : [];
        }
        if (assetTypeToLoaderMap.has(assetType)) {
            return assetTypeToLoaderMap.get(assetType)(...args);
        }
        throw new Error(`Asset type ${assetType} is not supported`);
    }

    export function getJson(url: string) {
        return cache.get(JsonAsset, url);
    }

    export async function loadJson(url: string, withTimestamp = true) {
        return (
            getCachedAsset(url, JsonAsset) ??
            new Promise<JsonAsset>((resolve) => {
                assetManager.loadRemote<JsonAsset>(withTimestamp ? urlWithTimestamp(url) : url, (err, asset) => {
                    if (err) {
                        error(err);
                        resolve(null);
                        return;
                    }
                    cache.set(JsonAsset, url, asset);
                    resolve(asset);
                });
            })
        );
    }

    export function getSpriteFrame(url: string) {
        return cache.get(SpriteFrame, url);
    }

    export async function loadSpriteFrame(url: string, withTimestamp = true) {
        return (
            getCachedAsset(url, SpriteFrame) ??
            new Promise<SpriteFrame>((resolve) => {
                // ensure the url has correct extension, otherwise asset manager will fail to load the image
                const ext = extname(url).toLowerCase() || '.png';
                assetManager.loadRemote<ImageAsset>(withTimestamp ? urlWithTimestamp(url) : url, { ext }, (err, asset) => {
                    if (err) {
                        error(err);
                        resolve(null);
                        return;
                    }
                    const spriteFrame = SpriteFrame.createWithImage(asset);
                    cache.set(SpriteFrame, url, spriteFrame);
                    resolve(spriteFrame);
                });
            })
        );
    }

    export function getSkeletonData(skeletonUrl: string) {
        return cache.get(sp.SkeletonData, skeletonUrl);
    }

    export async function loadSkeletonData(skeletonUrl: string, atlasUrl: string, imageUrls: string[], withTimestamp = true) {
        return (
            getCachedAsset(skeletonUrl, sp.SkeletonData) ??
            new Promise<sp.SkeletonData>((resolve) => {
                const textures: Texture2D[] = [];
                const textureNames: string[] = [];
                const skeletonPromise = new Promise<JsonAsset>((skeletonResolve, skeletonReject) => {
                    assetManager.loadRemote<JsonAsset>(withTimestamp ? urlWithTimestamp(skeletonUrl) : skeletonUrl, (err, data) => {
                        if (err) {
                            skeletonReject(err);
                            return;
                        }
                        skeletonResolve(data);
                    });
                });
                const atlasPromise = new Promise<TextAsset>((atlasResolve, atlasReject) => {
                    assetManager.loadRemote<TextAsset>(withTimestamp ? urlWithTimestamp(atlasUrl) : atlasUrl, (err, data) => {
                        if (err) {
                            atlasReject(err);
                            return;
                        }
                        atlasResolve(data);
                    });
                });
                const imagePromises = imageUrls.map(
                    (imageUrl) =>
                        new Promise<{ name: string; texture: Texture2D }>((imageResolve, imageReject) => {
                            assetManager.loadRemote<ImageAsset>(withTimestamp ? urlWithTimestamp(imageUrl) : imageUrl, (err, asset) => {
                                if (err) {
                                    imageReject(err);
                                    return;
                                }
                                const imageName = imageUrl.split('\\')[imageUrl.split('\\').length - 1].split('/')[imageUrl.split('/').length - 1];
                                const texture = new Texture2D();
                                texture.image = asset;
                                textures.push(texture);
                                textureNames.push(imageName);
                                imageResolve({ name: imageName, texture });
                            });
                        }),
                );

                Promise.all([skeletonPromise, atlasPromise, ...imagePromises]).then((data) => {
                    const skeletonData = new sp.SkeletonData();
                    skeletonData.skeletonJson = data[0].json as sp.spine.SkeletonJson;
                    skeletonData.atlasText = data[1].text;
                    skeletonData.textures = textures;
                    skeletonData.textureNames = textureNames;
                    cache.set(sp.SkeletonData, skeletonUrl, skeletonData);
                    resolve(skeletonData);
                });
            })
        );
    }

    export function getBitmapFont(fontUrl: string) {
        return cache.get(BitmapFont, fontUrl);
    }

    /**
     * @param fontUrl  - 只需輸入 xxx.fnt ， 圖片則須與 fnt 同名 ex: font_01.fnt
     * @param textureUrl - 圖片路徑，若不提供則會自動尋找與 fnt 同名的圖片
     */
    export async function loadBitmapFont(fontUrl: string, textureUrl?: string, withTimestamp = true) {
        return (
            getCachedAsset(fontUrl, BitmapFont) ??
            new Promise<BitmapFont>((resolve) => {
                assetManager.loadRemote<TextAsset>(withTimestamp ? urlWithTimestamp(fontUrl) : fontUrl, { ext: '.fnt' }, async (err, textAsset) => {
                    if (err) {
                        error(err);
                        resolve(null);
                        return;
                    }
                    if (!textAsset.text) {
                        error(err);
                        resolve(null);
                        return;
                    }
                    const bitmapFont = new BitmapFont();
                    const parser = FntParser.parseFontConfig(textAsset);
                    bitmapFont.fntConfig = parser;
                    const spriteFrame = await loadSpriteFrame(textureUrl ?? fontUrl.replace('.fnt', '.png'), withTimestamp);
                    if (!spriteFrame) {
                        error(`Failed to load font image for ${fontUrl}`);
                        resolve(null);
                        return;
                    }
                    bitmapFont.spriteFrame = spriteFrame;
                    // 因為資源是自己載入的，所以需要手動更新 onLoaded
                    bitmapFont.onLoaded();
                    cache.set(BitmapFont, fontUrl, bitmapFont);
                    resolve(bitmapFont);
                });
            })
        );
    }

    export function initSkeletonData(json: sp.spine.SkeletonJson, atlasText: string, textureInfos: { texture: Texture2D; name: string }[]) {
        const skeletonData = new sp.SkeletonData();
        skeletonData.skeletonJson = json;
        skeletonData.atlasText = atlasText;
        skeletonData.textures = textureInfos.map((info) => info.texture);
        skeletonData.textureNames = textureInfos.map((info) => info.name);
        return skeletonData;
    }
}

export default RemoteLoader;

if (DEV) {
    (window as any).RemoteLoader = RemoteLoader;
}
